/**
 * Whether this browser deletes the database on a timer, and which way out of
 * that it has.
 *
 * WebKit's Intelligent Tracking Prevention deletes *all* script-writable
 * storage — IndexedDB included — for a site that has gone seven days of
 * browser use without a visit. It is not eviction under storage pressure, so
 * `navigator.storage.persist()` (which `requestPersistentStorage` asks for on
 * the first import) does not stop it. For a local-first logbook that is the
 * whole record gone after a fortnight's leave.
 *
 * Chromium and Firefox have no such timer: they evict only under storage
 * pressure, and a persisted origin not even then. So the notice is for:
 *
 * - **iOS and iPadOS, in any browser.** Every iOS browser is WebKit, and ITP
 *   is on in WKWebView-based browsers too, so switching to Chrome there is no
 *   fix. The fix is a Home Screen web app, which WebKit exempts: it keeps its
 *   own storage and its own day counter, and that counter only runs while the
 *   app itself is in use.
 * - **Safari on macOS.** Add to Dock (Sonoma on) gives the same standalone
 *   exemption, or any other browser escapes ITP entirely.
 *
 * Already running standalone means already exempt, so nothing to say.
 */
export type StorageRisk = 'ios' | 'macos-safari';

export interface BrowserEnvironment {
  userAgent: string;
  /** iPadOS reports a Mac user agent; its touch points are what give it away. */
  maxTouchPoints: number;
  /** Launched from the Home Screen or the Dock rather than a browser tab. */
  standalone: boolean;
}

/*
 * Every Mac browser but Safari names itself, because they all still carry
 * "Safari/" in their user agent for compatibility. `Version/` is Safari's own
 * token; the list rules out the ones that borrow it or run on WebKit anyway.
 */
const NOT_SAFARI = /Chrome|Chromium|CriOS|FxiOS|EdgiOS|Edg\/|OPR\/|Firefox|Vivaldi|Brave/;

export function detectStorageRisk(env: BrowserEnvironment): StorageRisk | null {
  if (env.standalone) {
    return null;
  }
  const ua = env.userAgent;
  if (/iPhone|iPad|iPod/.test(ua) || (ua.includes('Macintosh') && env.maxTouchPoints > 1)) {
    return 'ios';
  }
  if (ua.includes('Macintosh') && /Version\/[\d.]+.*Safari\//.test(ua) && !NOT_SAFARI.test(ua)) {
    return 'macos-safari';
  }
  return null;
}

/** The live browser, read defensively: jsdom and older WebViews lack parts of it. */
export function currentBrowserEnvironment(): BrowserEnvironment {
  const nav = globalThis.navigator as (Navigator & { standalone?: boolean }) | undefined;
  const displayStandalone =
    typeof globalThis.matchMedia === 'function' &&
    globalThis.matchMedia('(display-mode: standalone)').matches;
  return {
    userAgent: nav?.userAgent ?? '',
    maxTouchPoints: nav?.maxTouchPoints ?? 0,
    standalone: nav?.standalone === true || displayStandalone,
  };
}
